import { DownloadCloud, RefreshCcw, Trash2 } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Image } from '@unistocks/components/basic/image';
import { Confirm } from '@unistocks/components/confirm/confirm';
import { Group } from '@unistocks/components/group/group';
import { Sheet } from '@unistocks/components/sheet/sheet';
import { toast } from '@unistocks/components/toast';
import { AuthenticateExternalAccountModal } from '@unistocks/features/external-accounts/modals/authenticate-external-account';
import { formatDate, t } from '@unistocks/localization';
import { ExternalAccountDto } from '@unistocks/proxy/axios-client';
import {
  externalAccountsGetAccountQueryKey,
  externalAccountsGetAccountsQueryKey,
  externalAccountsGetPortfoliosQueryKey,
  useExternalAccountsDeleteAccountMutationWithParameters,
  useExternalAccountsSyncExternalPortfoliosBankIdMutationWithParameters
} from '@unistocks/proxy/axios-client/ExternalAccountsQuery';
import { useToggle } from '@unistocks/shared/hooks/use-toggle';
import { Link } from 'expo-router';
import React, { useState } from 'react';
import { ListItem, Separator, useControllableState } from 'tamagui';

export type ExternalAccountsListProps = {
  externalAccounts: ExternalAccountDto[];
};

export const ExternalAccountsList: React.FC<ExternalAccountsListProps> = ({ externalAccounts }) => {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<ExternalAccountDto>();
  const [menuOpen, toggleMenu, setMenuOpen] = useToggle(false);

  const [syncOpen, onSyncOpenChange] = useControllableState({
    defaultProp: false,
    onChange: (open: boolean) => {
      if (!open) {
        setSelected(undefined);
      }
    }
  });

  const { mutate: syncExternalAccounts } =
    useExternalAccountsSyncExternalPortfoliosBankIdMutationWithParameters({
      onSuccess: (_, { body }) => {
        toast.info(t('::Sync:Notification:InProgress:Title'), {
          description: t('::Sync:Notification:InProgress:Message', {
            0: body.displayName
          }),
          duration: 8_000
        });
        onSyncOpenChange(false);
      }
    });

  const { mutate: deleteAccount, isPending: isPendingDelete } =
    useExternalAccountsDeleteAccountMutationWithParameters({
      onSuccess: (_res, { provider, accountIdentifier }) => {
        queryClient.invalidateQueries({ queryKey: externalAccountsGetAccountsQueryKey() });
        queryClient.invalidateQueries({
          queryKey: externalAccountsGetAccountQueryKey(provider, accountIdentifier)
        });
        queryClient.invalidateQueries({
          queryKey: externalAccountsGetPortfoliosQueryKey(provider, accountIdentifier)
        });
        toast.success(t('::ExternalAccounts:Delete:Success'));
        toggleMenu();
      }
    });

  const onAuthenticateSuccess = () => {
    const provider = selected?.provider;

    if (provider) {
      syncExternalAccounts({
        provider: provider.name,
        body: {
          accountIdentifier: provider.accountIdentifier,
          displayName: provider.displayName,
          portfolioId: null,
          providerIdentifier: provider.providerIdentifier,
          syncAll: false,
          username: null,
          useTestProviders: false
        }
      });
    }

    onSyncOpenChange(false);
  };

  const onAuthenticateError = () => {
    toast.error(t('::ExternalAccounts:Authentication:Error{0}', { 0: selected?.displayName }));
  };

  return (
    <>
      <Group render="ul" separator={<Separator />}>
        {externalAccounts.map((externalAccount, index) => (
          <Group.Item key={index}>
            <ListItem
              cursor="pointer"
              size={'$5'}
              gap={'$2'}
              borderRadius={'$2'}
              $md={{ size: '$4' }}
              title={externalAccount.displayName}
              subTitle={
                externalAccount.updated
                  ? `${t('::LastUpdate')} ${formatDate(externalAccount.updated, {
                      dateStyle: 'medium'
                    })}`
                  : ''
              }
              icon={<Image rounded size={'$3'} source={externalAccount.provider.imageUri} />}
              onPress={() => {
                setSelected(externalAccount);
                toggleMenu();
              }}
            />
          </Group.Item>
        ))}
      </Group>

      <Sheet
        size="small"
        open={menuOpen}
        onOpenChange={setMenuOpen}
        headerIcon={<Image rounded size={'$2.5'} source={selected?.provider.imageUri} />}
        headerTitle={selected?.displayName}
        scrollMode="view"
        enableDynamicSizing
      >
        {selected ? (
          <Group render="ul" mx={'$-3'} separator={<Separator />}>
            <Group.Item>
              <Link
                asChild
                href={{
                  pathname: '/(tabs)/(menu)/external-accounts/import',
                  params: {
                    provider: selected.provider.name,
                    identifier: selected.provider.providerIdentifier,
                    accountIdentifier: selected.provider.accountIdentifier
                  }
                }}
              >
                <ListItem
                  cursor="pointer"
                  size={'$5'}
                  gap={'$3'}
                  title={t('::Import:PopupMenu:Import')}
                  scaleIcon={1.5}
                  icon={<DownloadCloud color={'$success'} />}
                  onPress={toggleMenu}
                />
              </Link>
            </Group.Item>

            <Group.Item>
              <ListItem
                cursor="pointer"
                size={'$5'}
                gap={'$3'}
                title={t('::Import:PopupMenu:Sync')}
                scaleIcon={1.5}
                icon={<RefreshCcw color={'$unistocks'} />}
                onPress={() => {
                  toggleMenu();
                  onSyncOpenChange(true);
                }}
              />
            </Group.Item>

            <Group.Item>
              <Confirm
                title={t('::AreYouSure')}
                message={t('::ExternalAccounts:Delete')}
                confirmText={t('::Delete')}
                isLoading={isPendingDelete}
                onConfirm={() =>
                  deleteAccount({
                    provider: selected.provider.name,
                    accountIdentifier: selected.provider.accountIdentifier ?? undefined
                  })
                }
              >
                <ListItem
                  cursor="pointer"
                  size={'$5'}
                  gap={'$3'}
                  title={t('::Import:PopupMenu:Delete')}
                  scaleIcon={1.5}
                  icon={<Trash2 color={'$danger'} />}
                />
              </Confirm>
            </Group.Item>
          </Group>
        ) : null}
      </Sheet>

      {selected ? (
        <AuthenticateExternalAccountModal
          open={syncOpen}
          onOpenChange={onSyncOpenChange}
          provider={selected.provider}
          userIdentifier={selected.userIdentifier ?? undefined}
          onAuthenticateSuccess={onAuthenticateSuccess}
          onAuthenticateError={onAuthenticateError}
          headerSubtitle={t('::Import:PopupMenu:Sync')}
        />
      ) : null}
    </>
  );
};

ExternalAccountsList.displayName = 'ExternalAccountsList';
